import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useInterval } from 'usehooks-ts';

import { AlertMessage, Button, Spinner } from 'ui';
import { getSubscriptionDiscountAmount } from 'common/utils';

import SubscriptionLayout from './SubscriptionLayout';
import PaymentForm from '../signup/PaymentForm';
import appSettings from '../../app-settings';
import { useAccount } from '../../store/account/hooks';
import SubscriptionSummary from '../signup/SubscriptionSummary';
import { ONBOARDING_STEPS } from '../../core/constants';

const stripePromise = loadStripe(appSettings.stripePublishableKey);
const POLLING_INTERVAL_IN_MS = 1000;
const CONFIRMATION_STATUSES = {
  pending: 'pending',
  succeeded: 'succeeded',
};

const SubscribeStep = ({ setStep }) => {
  const [error, setError] = useState('');
  const [confirmationStatus, setConfirmationStatus] = useState('');
  const { account, getAccount } = useAccount();

  const baseDiscount = account?.subscription?.discounts?.base;

  const orderSubTotal =
    Object.values(account?.subscription?.items).reduce(
      (acc, item) => acc + (item.enabled ? item.yearlyBasePrice : 0),
      0,
    ) / (account?.subscription?.interval === 'yearly' ? 1 : 12);

  const orderDiscount = getSubscriptionDiscountAmount(baseDiscount, orderSubTotal);

  const orderTotal = orderSubTotal - orderDiscount;

  const options = {
    mode: 'subscription',
    amount: orderTotal,
    currency: 'usd',
    appearance: {
      variables: {
        spacingUnit: '3px',
        fontSizeBase: '14px',
      },
      rules: {
        '.TermsText': {
          lineHeight: '1.25',
        },
      },
    },
    // paymentMethodTypes: ['card', 'us_bank_account'],
    // paymentMethodOptions: {
    //   us_bank_account: {
    //     verification_method: 'instant', // Disable delayed verification via microdeposits for MVP. Might allow later
    //   },
    // },
  };

  useEffect(() => {
    if (confirmationStatus === CONFIRMATION_STATUSES.pending) {
      const { subscriptionStatus } = account;

      if (subscriptionStatus === 'active') {
        setConfirmationStatus(CONFIRMATION_STATUSES.succeeded);
      } else {
        console.log('subscriptionStatus', subscriptionStatus);
      }
    }
  }, [account, confirmationStatus]);

  useInterval(getAccount, confirmationStatus === CONFIRMATION_STATUSES.pending ? POLLING_INTERVAL_IN_MS : null);

  return (
    <SubscriptionLayout>
      <div className="relative flex size-full flex-col items-center justify-center">
        <div className="space-y-8">
          {!confirmationStatus && (
            <>
              <h1 className="text-h2 font-semibold">Subscription Order Form</h1>
              <Elements stripe={stripePromise} options={options}>
                <PaymentForm onSuccess={() => setConfirmationStatus(CONFIRMATION_STATUSES.pending)}>
                  <SubscriptionSummary
                    orderTotal={orderTotal}
                    orderSubTotal={orderSubTotal}
                    orderDiscount={orderDiscount}
                    orderDiscountName={baseDiscount?.name}
                  />
                </PaymentForm>
              </Elements>
            </>
          )}

          {confirmationStatus === CONFIRMATION_STATUSES.pending && (
            <div className="flex flex-col items-center space-y-8">
              <h2 className="text-h2 font-semibold">Confirming your subscription...</h2>
              <Spinner />
            </div>
          )}

          {confirmationStatus === CONFIRMATION_STATUSES.succeeded && (
            <div className="max-w-[336px] space-y-4">
              <h1 className="text-h2 font-semibold">Success!</h1>
              <p className="text-lg">Your DonorSpring subscription has been confirmed.</p>
              <Button
                title="Continue"
                color="primary"
                size="xl"
                className="!block w-full"
                onClick={() => setStep(ONBOARDING_STEPS.welcome)}
              />
            </div>
          )}
        </div>
      </div>

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={() => setError(false)}
        severity="error"
      />
    </SubscriptionLayout>
  );
};

SubscribeStep.propTypes = {
  setStep: PropTypes.func.isRequired,
};

export default SubscribeStep;
